import React, { useEffect, useRef } from "react";

const StarfieldBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const starsRef = useRef([]);
  const shootingStarRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const layerCount = 3;
    const speeds = [0.02, 0.04, 0.08];
    const baseStarCount = 30;

    // Resize the canvas
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      createStars();
    };

    // Create the starfield
    const createStars = () => {
      starsRef.current = [];
      const scalingFactor = Math.max(canvas.width, canvas.height) / 1000;

      for (let i = 0; i < layerCount; i++) {
        const starCount = Math.floor(baseStarCount * scalingFactor * (i + 1));
        for (let j = 0; j < starCount; j++) {
          let starSize;
          if (i === 0) starSize = Math.random() * 0.3 + 0.2;
          else if (i === 1) starSize = Math.random() * 0.4 + 0.3;
          else starSize = Math.random() * 0.6 + 0.4;

          starsRef.current.push({
            x: Math.random() * (canvas.width + 200) - 100,
            y: Math.random() * (canvas.height + 200) - 100,
            size: starSize,
            speed: speeds[i],
            opacity: Math.random(),
            baseOpacity: Math.random() * 0.5 + 0.5,
            twinkleSpeed: Math.random() * 0.0005 + 0.0002,
            layer: i,
          });
        }
      }
    };

    // Update star positions
    const updateStars = () => {
      starsRef.current.forEach((star) => {
        star.x += star.speed * 0.7;
        star.y += star.speed * 0.4;

        star.opacity =
          star.baseOpacity + Math.sin(Date.now() * star.twinkleSpeed) * 0.3;

        if (star.x > canvas.width + 100 || star.y > canvas.height + 100) {
          if (Math.random() < 0.5) {
            star.x = Math.random() * 200 - 200;
            star.y = Math.random() * canvas.height;
          } else {
            star.x = Math.random() * canvas.width;
            star.y = Math.random() * 200 - 200;
          }
        }
      });
    };

    // Draw the stars
    const drawStars = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Cosmic background with gradients
      const centerGradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        Math.max(canvas.width, canvas.height) / 2
      );
      centerGradient.addColorStop(0, "rgba(5, 10, 25, 1)");
      centerGradient.addColorStop(0.4, "rgba(2, 5, 15, 1)");
      centerGradient.addColorStop(1, "rgba(0, 0, 5, 1)");

      ctx.fillStyle = centerGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Nebula effect
      const nebulaGradient = ctx.createRadialGradient(
        canvas.width * 0.3,
        canvas.height * 0.7,
        0,
        canvas.width * 0.3,
        canvas.height * 0.7,
        canvas.width * 0.4
      );
      nebulaGradient.addColorStop(0, "rgba(10, 5, 20, 0.3)");
      nebulaGradient.addColorStop(1, "rgba(0, 0, 0, 0)");

      ctx.fillStyle = nebulaGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw stars as circles
      starsRef.current.forEach((star) => {
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // Create shooting star
    const createShootingStar = () => {
      const startX = Math.random() * canvas.width * 0.2;
      const startY = Math.random() * canvas.height * 0.2;
      const endX = startX + canvas.width * 0.6 + Math.random() * 200;
      const endY = startY + canvas.height * 0.6 + Math.random() * 200;

      const dx = endX - startX;
      const dy = endY - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const speed = 1.5;
      const duration = distance / speed;

      shootingStarRef.current = {
        x: startX,
        y: startY,
        dx: dx / duration,
        dy: dy / duration,
        length: Math.random() * 150 + 80,
        opacity: 0,
        maxOpacity: Math.random() * 0.8 + 0.2,
        fadeIn: true,
        fadeOut: false,
        life: 0,
        maxLife: duration,
        fadeInDuration: duration * 0.2,
        fadeOutStart: duration * 0.7,
      };

      const nextAppearance = Math.random() * 15000 + 10000;
      setTimeout(createShootingStar, nextAppearance);
    };

    // Update shooting star
    const updateShootingStar = () => {
      if (!shootingStarRef.current) return;

      const shootingStar = shootingStarRef.current;
      shootingStar.x += shootingStar.dx;
      shootingStar.y += shootingStar.dy;
      shootingStar.life++;

      if (
        shootingStar.fadeIn &&
        shootingStar.life < shootingStar.fadeInDuration
      ) {
        shootingStar.opacity =
          (shootingStar.life / shootingStar.fadeInDuration) *
          shootingStar.maxOpacity;
      } else if (shootingStar.fadeIn) {
        shootingStar.fadeIn = false;
        shootingStar.opacity = shootingStar.maxOpacity;
      }

      if (
        shootingStar.life > shootingStar.fadeOutStart &&
        !shootingStar.fadeOut
      ) {
        shootingStar.fadeOut = true;
      }

      if (shootingStar.fadeOut) {
        const fadeProgress =
          (shootingStar.life - shootingStar.fadeOutStart) /
          (shootingStar.maxLife - shootingStar.fadeOutStart);
        shootingStar.opacity = shootingStar.maxOpacity * (1 - fadeProgress);
      }

      if (
        shootingStar.life >= shootingStar.maxLife ||
        shootingStar.x > canvas.width + 100 ||
        shootingStar.y > canvas.height + 100
      ) {
        shootingStarRef.current = null;
      }
    };

    // Draw shooting star
    const drawShootingStar = () => {
      if (!shootingStarRef.current) return;

      const shootingStar = shootingStarRef.current;
      const gradient = ctx.createLinearGradient(
        shootingStar.x,
        shootingStar.y,
        shootingStar.x - (shootingStar.dx * shootingStar.length) / 2,
        shootingStar.y - (shootingStar.dy * shootingStar.length) / 2
      );
      gradient.addColorStop(0, `rgba(255, 255, 255, ${shootingStar.opacity})`);
      gradient.addColorStop(
        0.5,
        `rgba(255, 255, 255, ${shootingStar.opacity * 0.5})`
      );
      gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);

      ctx.beginPath();
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.moveTo(shootingStar.x, shootingStar.y);
      ctx.lineTo(
        shootingStar.x - (shootingStar.dx * shootingStar.length) / 2,
        shootingStar.y - (shootingStar.dy * shootingStar.length) / 2
      );
      ctx.stroke();
      ctx.closePath();
    };

    // Animation loop
    const animate = () => {
      updateStars();
      updateShootingStar();
      drawStars();
      drawShootingStar();
      animationRef.current = requestAnimationFrame(animate);
    };

    // Initialize
    resizeCanvas();
    createStars();
    setTimeout(createShootingStar, Math.random() * 5000 + 3000);
    animate();

    // Event listeners
    window.addEventListener("resize", resizeCanvas);

    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none"
      style={{
        zIndex: -1,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
      }}
    />
  );
};

export default StarfieldBackground;
